import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { Divider, FormHelperText, Grid, Typography } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTextField from "../../../components/CustomTextField";
import Select from "../../../components/Select/Select";
import {
  createClinicSlots,
  deleteClinicHolidayById,
  getClinicDetails,
  getClinicHolidays,
  getClinicOpeningHours,
  updateClinic,
  updateClinicHolidays,
  updateClinicOpeningHours,
} from "../../../redux/reducers/clinicSlice";
import {
  getEmrSettings,
  updateEmrSettings,
} from "../../../redux/reducers/globalSettingsSlice";
import { weekArray } from "../../../util/arrayList";
import { getSessionData, validateTimeSlots } from "../../../util/function";
import {
  patchMandateSession,
  readMandateSession,
} from "../../../util/mandateSessionStorage";
import { initSlot } from "../../../util/object";
import "../BranchManagement/BranchManagement.css";
import "./GlobalSettings.css";

const toMandateList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const editConsultRoleOptions = [
  { code: "BRANCH_MANAGER", label: "Practice Manager" },
  { code: "CLINIC_OWNER", label: "Clinic Owner" },
  { code: "ROLE_CLINICSUPERADMIN", label: "Super Admin" },
];

const EDIT_CONSULT_ROLE_TO_USER_TYPES = {
  BRANCH_MANAGER: ["ROLE_CLINICADMIN"],
  CLINIC_OWNER: ["ROLE_CLINIC"],
  ROLE_CLINICSUPERADMIN: ["ROLE_CLINICSUPERADMIN"],
};

const isOwnerLevelRole = (role) =>
  role === "CLINIC_OWNER" || role === "ROLE_CLINICSUPERADMIN";

const isClinicOwnerOnly = (roles) => {
  const list = Array.isArray(roles) ? roles : [];
  return list?.length > 0 && list?.every(isOwnerLevelRole);
};

const getUserOptionId = (user) => {
  if (user == null) return "";
  try {
    if (typeof user === "object") {
      return String(
        user?.value ?? user?.id ?? user?.userId ?? user?.doctorId ?? "",
      );
    }
    return String(user);
  } catch {
    return "";
  }
};

const getUserRoleNames = (user) => {
  try {
    const types = user?.userType || user?.roles || user?.roleList || [];
    if (!Array.isArray(types)) return [];
    return types
      ?.map((role) => (typeof role === "string" ? role : role?.name))
      ?.filter(Boolean);
  } catch {
    return [];
  }
};

const toUserIds = (users) => {
  if (!Array.isArray(users)) return [];
  return users
    ?.map((user) => Number(getUserOptionId(user)))
    ?.filter((id) => Number.isFinite(id) && id > 0);
};

const toStringList = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? "")).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v ?? "")).filter(Boolean);
      }
    } catch {
      // plain comma-separated fallback
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
  }
  return [];
};

const toBoolFlag = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "1" || v === "y" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "n" || v === "no" || v === "") {
      return false;
    }
  }
  return !!value;
};

const normalizeEditConsultAccess = (access) => ({
  roles: toStringList(access?.roles),
  users: toStringList(access?.users),
});

const safeJsonStringify = (value, fallback = "[]") => {
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
};

const serviceTypes = [
  "Consultation",
  "Grooming",
  "Lab Tests",
  "Surgery",
  "Vaccination",
];
const consentOptions = [
  "General Consent",
  "Surgical Consent",
  "Anesthesia Consent",
  "Lab Consent",
  "Boarding Consent",
];
const colorPalette = [
  "#0054A6",
  "#7C3AED",
  "#EA580C",
  "#DC2626",
  "#16A34A",
  "#0891B2",
  "#DB2777",
  "#CA8A04",
];

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 999,
  border: "1px solid #D1D5DB",
  padding: "8px 12px",
  cursor: "pointer",
  background: "#fff",
  userSelect: "none",
};

const entitiesList = [
  { id: "clinic", label: "Clinic" },
  { id: "pharmacy", label: "Pharmacy" },
  { id: "petshop", label: "Pet Shop" },
];

const consultTypeOptions = [
  {
    key: "virtual",
    label: "Virtual",
    term: "Virtual",
    Icon: VideocamOutlinedIcon,
  },
  {
    key: "physical",
    label: "Physical",
    term: "Physical",
    Icon: MedicalServicesOutlinedIcon,
  },
  {
    key: "homeVisit",
    label: "Home Visit",
    term: "Home",
    Icon: HomeOutlinedIcon,
  },
];

const emrFieldRows = [
  { key: "admissionRemarks", label: "Admission Remarks" },
  { key: "redFlags", label: "Red Flags / Inpatient Alerts" },
  { key: "inpatientInstructions", label: "Inpatient Instructions" },
  { key: "soapNotes", label: "SOAP Notes" },
  { key: "procedures", label: "Procedures" },
  { key: "administered", label: "Administered" },
  { key: "notes", label: "Memo" },
  { key: "preventive", label: "Preventive" },
  { key: "diagnostics", label: "Diagnostics" },
  { key: "prescription", label: "Prescription" },
  { key: "consentForms", label: "Consent Form" },
  { key: "protocolForms", label: "Protocol Form" },
  { key: "ioMonitoring", label: "I/O Monitoring" },
  { key: "vitals", label: "Vitals" },
  { key: "bodyMap", label: "Body Map" },
  { key: "files", label: "Files" },
];

const initialEmrFields = {
  admissionRemarks: true,
  redFlags: true,
  inpatientInstructions: true,
  soapNotes: true,
  procedures: true,
  administered: true,
  notes: true,
  preventive: true,
  diagnostics: true,
  prescription: true,
  consentForms: true,
  protocolForms: true,
  ioMonitoring: true,
  vitals: true,
  bodyMap: true,
  files: true,
};

const normalizeEmrFields = (payload = {}) => {
  const next = { ...initialEmrFields };
  Object.keys(initialEmrFields).forEach((key) => {
    if (payload?.[key] != null) next[key] = !!payload[key];
  });
  return next;
};

const stepperBtn = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #D1D5DB",
  background: "#fff",
  cursor: "pointer",
  fontSize: 18,
  lineHeight: 1,
  color: "#111827",
};

const sectionHeaderStyle = {
  color: "#6B7280",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontSize: 11,
};

const ConsultationSettingsScreen = () => {
  const dispatch = useDispatch();
  const {
    openingHours,
    details: clinicDet,
    holidays,
  } = useSelector((state) => state?.clinic);
  const user = localStorage.getItem("user");
  let profile = {};
  try {
    profile = user ? JSON.parse(user) : {};
  } catch {
    profile = {};
  }
  const branches = useMemo(() => {
    const sessionBranches = readMandateSession()?.clinicBranches;
    return Array.isArray(sessionBranches) ? sessionBranches : [];
  }, []);
  const globalSettingsData =
    readMandateSession()?.dataSharing?.globalSettingsData ?? false;
  const showSharedTag =
    branches?.some((li) => li?.id === profile?.id && li?.branchNumber) &&
    (globalSettingsData === true || globalSettingsData === "true");

  const [autoApproveOnlineBook, setAutoApproveOnlineBook] = useState(true);
  const [onlineBookAccess, setOnlineBookAccess] = useState({
    roles: [],
    users: [],
  });
  const [onlineBookAccessError, setOnlineBookAccessError] = useState(false);
  const [autoCloseDays, setAutoCloseDays] = useState(3);
  const [allowEditConsultations, setAllowEditConsultations] = useState(false);
  const [editConsultAccess, setEditConsultAccess] = useState({
    roles: [],
    users: [],
  });
  const [editConsultAccessError, setEditConsultAccessError] = useState(false);
  const [activeEntity, setActiveEntity] = useState("clinic");
  const [entityProfiles, setEntityProfiles] = useState({
    clinic: { name: "", taxId: "", address: "" },
    pharmacy: { name: "", taxId: "", address: "", dlNo: "" },
    petshop: { name: "", taxId: "", address: "" },
  });
  const [consultTypes, setConsultTypes] = useState({
    virtual: false,
    physical: false,
    homeVisit: false,
  });
  const [patientIdSeries, setPatientIdSeries] = useState("");
  const [temperatureUnit, setTemperatureUnit] = useState("C");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [dayValues, setDayValues] = useState(weekArray);
  const [duration, setDuration] = useState("15");
  const [noSlotErr, setNoSlotErr] = useState(false);
  const [timeErr, setTimeErr] = useState(false);
  const [occasionError, setOccasionError] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState(null);
  const [newHolidayOccasion, setNewHolidayOccasion] = useState("");
  const [consentMapping, setConsentMapping] = useState({
    Consultation: ["General Consent"],
    Grooming: [],
    "Lab Tests": ["Lab Consent"],
    Surgery: ["Surgical Consent", "Anesthesia Consent"],
    Vaccination: [],
  });
  const [serviceColors, setServiceColors] = useState({
    Consultation: "#0054A6",
    Grooming: "#7C3AED",
    "Lab Tests": "#0891B2",
    Surgery: "#DC2626",
    Vaccination: "#16A34A",
  });
  const [colorPickerFor, setColorPickerFor] = useState(null);
  const colorPickerRef = useRef(null);
  const [emrFields, setEmrFields] = useState(initialEmrFields);

  const usersList = useMemo(() => {
    const list = getSessionData()?.vets ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const getUsersForRoles = useCallback(
    (roles = []) => {
      const roleList = Array.isArray(roles) ? roles.filter(Boolean) : [];
      if (!roleList.length) return [];
      try {
        const allowedTypeSet = new Set(
          roleList.flatMap(
            (role) => EDIT_CONSULT_ROLE_TO_USER_TYPES[role] || [role],
          ),
        );

        return (Array.isArray(usersList) ? usersList : [])
          ?.filter((user) => {
            const roleNames = getUserRoleNames(user);
            if (!roleNames.length) return false;
            return roleNames.some((name) => allowedTypeSet.has(name));
          })
          ?.map((user) => {
            const id = user?.id ?? user?.userId ?? user?.doctorId;
            return {
              name:
                user?.name ||
                user?.userName ||
                user?.fullName ||
                `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                `User ${id}`,
              value: String(id ?? ""),
            };
          })
          ?.filter((user) => user.value && user.value !== "undefined");
      } catch {
        return [];
      }
    },
    [usersList],
  );

  const logEditConsultInput = (nextAllow, nextAccess) => {
    try {
      const normalized = normalizeEditConsultAccess(nextAccess);
      console.log("editConsultationAccess input", {
        allowEditConsultations: !!nextAllow,
        roles: normalized.roles,
        userIds: toUserIds(normalized.users),
      });
    } catch {
      // ignore logging errors
    }
  };

  const toggleAllowEditConsultations = () => {
    setAllowEditConsultations((prev) => {
      const next = !prev;
      if (!next) {
        const cleared = { roles: [], users: [] };
        setEditConsultAccess(cleared);
        setEditConsultAccessError(false);
        logEditConsultInput(false, cleared);
      } else {
        logEditConsultInput(true, editConsultAccess);
      }
      return next;
    });
  };

  const toggleEditConsultRole = (role) => {
    if (!role) return;
    setEditConsultAccess((prev) => {
      const current = normalizeEditConsultAccess(prev);
      const exists = current.roles.includes(role);
      const nextRoles = exists
        ? current.roles.filter((r) => r !== role)
        : [...current.roles, role];
      const allowedUsers = new Set(
        getUsersForRoles(nextRoles).map((u) => String(u.value)),
      );
      const next = {
        roles: nextRoles,
        users: isClinicOwnerOnly(nextRoles)
          ? []
          : current.users.filter((u) => allowedUsers.has(getUserOptionId(u))),
      };
      if (next.roles.length || next.users.length) {
        setEditConsultAccessError(false);
      }
      logEditConsultInput(true, next);
      return next;
    });
  };

  const updateEditConsultUsers = (users) => {
    setEditConsultAccess((prev) => {
      const current = normalizeEditConsultAccess(prev);
      const nextUsers = (Array.isArray(users) ? users : [])
        ?.map((user) => getUserOptionId(user))
        ?.filter((id) => id && id !== "undefined" && id !== "null");
      const next = {
        ...current,
        users: nextUsers,
      };
      if (next.roles.length || next.users.length) {
        setEditConsultAccessError(false);
      }
      logEditConsultInput(true, next);
      return next;
    });
  };

  const toggleAutoApproveOnlineBook = (nextAutoApprove) => {
    setAutoApproveOnlineBook(!!nextAutoApprove);
    if (nextAutoApprove) {
      setOnlineBookAccess({ roles: [], users: [] });
      setOnlineBookAccessError(false);
    }
  };

  const toggleOnlineBookRole = (role) => {
    if (!role) return;
    setOnlineBookAccess((prev) => {
      const current = normalizeEditConsultAccess(prev);
      const exists = current.roles.includes(role);
      const nextRoles = exists
        ? current.roles.filter((r) => r !== role)
        : [...current.roles, role];
      const allowedUsers = new Set(
        getUsersForRoles(nextRoles).map((u) => String(u.value)),
      );
      const next = {
        roles: nextRoles,
        users: isClinicOwnerOnly(nextRoles)
          ? []
          : current.users.filter((u) => allowedUsers.has(getUserOptionId(u))),
      };
      if (next.roles.length || next.users.length) {
        setOnlineBookAccessError(false);
      }
      return next;
    });
  };

  const updateOnlineBookUsers = (users) => {
    setOnlineBookAccess((prev) => {
      const current = normalizeEditConsultAccess(prev);
      const nextUsers = (Array.isArray(users) ? users : [])
        ?.map((user) => getUserOptionId(user))
        ?.filter((id) => id && id !== "undefined" && id !== "null");
      const next = {
        ...current,
        users: nextUsers,
      };
      if (next.roles.length || next.users.length) {
        setOnlineBookAccessError(false);
      }
      return next;
    });
  };

  const loadEmrSettings = () => {
    dispatch(getEmrSettings({ silent: true })).then((res) => {
      if (getEmrSettings.fulfilled.match(res) && res?.payload) {
        setEmrFields(normalizeEmrFields(res.payload));
      }
    });
  };

  const refreshOpeningHoursAndPatchMandate = () => {
    try {
      dispatch(getClinicOpeningHours()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null || payload === false) return;
          patchMandateSession({
            clinicHours: toMandateList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore opening hours refresh errors
    }
  };

  const refreshHolidaysAndPatchMandate = () => {
    try {
      dispatch(getClinicHolidays()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null || payload === false) return;
          patchMandateSession({
            clinicHolidays: toMandateList(payload),
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore holidays refresh errors
    }
  };

  const loadClinicProfile = () => {
    try {
      dispatch(getClinicDetails()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null || payload === false) return;
          const prevClinic = readMandateSession()?.clinic ?? {};
          patchMandateSession({
            clinic: {
              ...prevClinic,
              ...payload,
              temperature:
                payload?.temperature ??
                payload?.temperatureUnit ??
                prevClinic?.temperature ??
                prevClinic?.temperatureUnit,
              weight:
                payload?.weight ??
                payload?.weightUnit ??
                prevClinic?.weight ??
                prevClinic?.weightUnit,
            },
          });
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore clinic load errors
    }
  };

  useEffect(() => {
    refreshOpeningHoursAndPatchMandate();
    refreshHolidaysAndPatchMandate();
    loadClinicProfile();
    loadEmrSettings();
  }, []);

  useEffect(() => {
    if (!clinicDet) return;
    const terms = {
      virtual: false,
      physical: false,
      homeVisit: false,
    };
    clinicDet?.clinicTerms?.forEach((trm) => {
      if (trm?.status !== "Y") return;
      if (trm?.consultationType === "Virtual") terms.virtual = true;
      if (trm?.consultationType === "Physical") terms.physical = true;
      if (trm?.consultationType === "Home") terms.homeVisit = true;
    });
    setConsultTypes(terms);
    if (clinicDet?.autoApproveOnlineBook != null) {
      setAutoApproveOnlineBook(
        toBoolFlag(clinicDet?.autoApproveOnlineBook, true),
      );
    } else if (clinicDet?.allowOnlineBook != null) {
      // Legacy: previously allowOnlineBook meant bookings were auto-approved
      setAutoApproveOnlineBook(toBoolFlag(clinicDet?.allowOnlineBook, true));
    }
    try {
      setOnlineBookAccess({
        roles: toStringList(clinicDet?.onlineBookAllowedRoles),
        users: toStringList(clinicDet?.onlineBookAllowedUsers),
      });
    } catch {
      setOnlineBookAccess({ roles: [], users: [] });
    }
    const closeDays = Number(clinicDet?.autoCloseAfterDays);
    if (Number.isFinite(closeDays) && closeDays > 0) {
      setAutoCloseDays(Math.max(1, Math.min(5, closeDays)));
    }
    try {
      const allowEdit = toBoolFlag(clinicDet?.allowEditConsultations, false);
      setAllowEditConsultations(allowEdit);
      setEditConsultAccess({
        roles: toStringList(
          clinicDet?.editConsultationRoles ?? clinicDet?.editConsultRoles,
        ),
        users: toStringList(
          clinicDet?.editConsultationUsers ??
            clinicDet?.editConsultationUserIds ??
            clinicDet?.editConsultUsers,
        ),
      });
    } catch {
      setAllowEditConsultations(false);
      setEditConsultAccess({ roles: [], users: [] });
    }
    const temp = String(clinicDet?.temperature ?? "C").trim();
    setTemperatureUnit(/f/i.test(temp) ? "F" : "C");
    const weight = String(clinicDet?.weight ?? "kg")
      ?.trim()
      ?.toLowerCase();
    setWeightUnit(weight === "lb" || weight === "lbs" ? "lb" : "kg");
    setPatientIdSeries(
      clinicDet?.patientIdSeries == null
        ? ""
        : String(clinicDet.patientIdSeries),
    );
    setEntityProfiles({
      clinic: {
        name: clinicDet?.name ?? "",
        taxId: clinicDet?.taxId ?? "",
        address: clinicDet?.address ?? "",
      },
      pharmacy: {
        name: clinicDet?.pharmacyName ?? "",
        taxId: clinicDet?.pharmacyTaxId ?? "",
        address: clinicDet?.pharmacyAddress ?? "",
        dlNo: clinicDet?.dlNo ?? "",
      },
      petshop: {
        name: clinicDet?.petShopName ?? "",
        taxId: clinicDet?.petShopTaxId ?? "",
        address: clinicDet?.petShopAddress ?? "",
      },
    });
  }, [clinicDet]);

  useEffect(() => {
    getRequiredDateList();
  }, [openingHours]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target)
      ) {
        setColorPickerFor(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const getRequiredDateList = () => {
    if (openingHours?.length > 0) {
      let loadedDuration = null;
      const reqArr = weekArray?.map((day) => {
        const dbDay = openingHours?.find((pa) => pa?.day === day?.value);
        if (dbDay) {
          const reqTimes = dbDay?.times?.map((tm, i) => {
            const [hour, min] = tm?.startTime?.split(":") ?? [];
            const [endHour, endMin] = tm?.endTime?.split(":") ?? [];
            return {
              startTime: new Date(new Date().setHours(hour, min)),
              endTime: new Date(new Date().setHours(endHour, endMin)),
              i,
            };
          });
          if (loadedDuration == null && dbDay?.duration != null) {
            loadedDuration = String(dbDay.duration);
          }
          return {
            ...day,
            isSelected: dbDay?.status,
            slots: reqTimes,
          };
        }
        return day;
      });
      if (loadedDuration != null) {
        setDuration(loadedDuration);
      }
      setDayValues(reqArr);
    }
  };

  const updateEntityProfile = (field, val) => {
    setEntityProfiles((prev) => ({
      ...prev,
      [activeEntity]: { ...(prev?.[activeEntity] ?? {}), [field]: val },
    }));
  };

  const handleDaySelect = (ind, value) => {
    const reqValues = dayValues?.map((pd, i) => {
      if (i === ind) return { ...pd, isSelected: value };
      return pd;
    });
    setDayValues(reqValues);
    if (reqValues?.some((rv) => rv?.isSelected)) {
      setNoSlotErr(false);
    } else {
      setNoSlotErr(true);
    }
    if (
      validateTimeSlots(
        reqValues?.filter((rv) => rv?.isSelected),
        duration,
      )
    ) {
      setTimeErr(false);
    } else {
      setTimeErr(true);
    }
  };

  const handleChangeDateValue = (name, value, mainInd, secInd) => {
    const reqValue = dayValues?.map((vd, i) => {
      if (i === mainInd) {
        return {
          ...vd,
          slots: vd?.slots?.map((slt, ind) => {
            if (ind === secInd) {
              return { ...slt, [name]: value };
            }
            return slt;
          }),
        };
      }
      return vd;
    });
    setDayValues(reqValue);
    if (
      validateTimeSlots(
        reqValue?.filter((rv) => rv?.isSelected),
        duration,
      )
    ) {
      setTimeErr(false);
    } else {
      setTimeErr(true);
    }
  };

  const handleDeleteDateRow = (i, ind) => {
    const reqValue = dayValues?.map((vd, index) => {
      if (i === index) {
        return {
          ...vd,
          slots: vd?.slots?.filter((slt, index2) => index2 !== ind),
        };
      }
      return vd;
    });
    setDayValues(reqValue);
  };

  const handleAddDateRow = (i) => {
    const reqValue = dayValues?.map((vd, ind) => {
      if (i === ind) {
        return {
          ...vd,
          slots: [...vd?.slots, { ...initSlot, i: vd?.slots?.length }],
        };
      }
      return vd;
    });
    setDayValues(reqValue);
  };

  const handleAddHoliday = async () => {
    if (!newHolidayDate) return;
    if (!String(newHolidayOccasion).trim()) {
      setOccasionError(true);
      return;
    }
    setOccasionError(false);
    const apiRes = await dispatch(
      updateClinicHolidays({
        date: moment(new Date(newHolidayDate)).format("YYYY-MM-DD"),
        holliday: String(newHolidayOccasion).trim(),
      }),
    );
    if (apiRes?.payload) {
      refreshHolidaysAndPatchMandate();
      setNewHolidayDate(null);
      setNewHolidayOccasion("");
    }
  };

  const handleRemoveHoliday = (holId) => {
    if (!holId) return;
    dispatch(deleteClinicHolidayById(holId)).then((res) => {
      if (res?.payload) {
        refreshHolidaysAndPatchMandate();
      }
    });
  };

  const toggleConsent = (service, consent) => {
    setConsentMapping((prev) => {
      const current = Array.isArray(prev?.[service]) ? prev[service] : [];
      const exists = current.includes(consent);
      return {
        ...prev,
        [service]: exists
          ? current.filter((c) => c !== consent)
          : [...current, consent],
      };
    });
  };

  const handleGlobalTimeSave = async () => {
    if (
      validateTimeSlots(
        dayValues?.filter((d) => d?.isSelected),
        duration,
      )
    ) {
      setTimeErr(false);
      const data = dayValues?.map((d) => {
        return {
          status: d?.isSelected,
          day: d?.value,
          times: d?.slots?.map((slt) => ({
            startTime: moment(new Date(slt?.startTime)).format("HH:mm"),
            endTime: moment(new Date(slt?.endTime)).format("HH:mm"),
          })),
          duration,
        };
      });
      if (data?.some((dt) => dt?.status)) {
        setNoSlotErr(false);
        dispatch(updateClinicOpeningHours({ data })).then((res) => {
          if (res?.payload) {
            const slotData = {
              type: "physical",
              dayRequest: dayValues?.map((li) => ({
                day: li?.value,
                times: li?.slots?.map((slt) => ({
                  startTime:
                    moment(new Date(slt?.startTime)).format("HH:mm") ?? "00:00",
                  endTime:
                    moment(new Date(slt?.endTime)).format("HH:mm") ?? "00:00",
                })),
                status: li?.isSelected,
              })),
              duration,
            };
            dispatch(
              createClinicSlots({ vetId: profile?.id, data: slotData }),
            ).then((apiRes) => {
              if (apiRes?.payload) {
                refreshOpeningHoursAndPatchMandate();
              }
            });
          }
        });
      } else {
        setNoSlotErr(true);
      }
    } else {
      setTimeErr(true);
    }
  };

  const handleClinicProfileSave = () => {
    const selectedTerms = consultTypeOptions
      .filter(({ key }) => consultTypes?.[key])
      .map(({ term }) => term);

    const reqList = ["Physical", "Virtual", "Home"]?.map((term) => {
      const selectedTerm = clinicDet?.clinicTerms?.find(
        (trm) => trm?.consultationType === term,
      );
      const reqObj = {
        consultationType: term,
        paymentType: selectedTerm?.paymentType ?? "prepaid",
        termsfront: selectedTerm?.termsfront ?? "50",
        refundType: selectedTerm?.refundType ?? "Y",
        refund: selectedTerm?.refund ?? "100",
        status: selectedTerms?.includes(term) ? "Y" : "N",
      };
      if (selectedTerm?.termId) reqObj.termId = selectedTerm?.termId;
      return reqObj;
    });

    const form = new FormData();
    form.append("name", entityProfiles?.clinic?.name ?? clinicDet?.name ?? "");
    form.append("email", clinicDet?.email ?? "");
    form.append("mobile", clinicDet?.mobile ?? "");
    form.append("location", clinicDet?.location ?? "");
    form.append("taxId", entityProfiles?.clinic?.taxId ?? "");
    form.append("pharmacyTaxId", entityProfiles?.pharmacy?.taxId ?? "");
    form.append("petShopTaxId", entityProfiles?.petshop?.taxId ?? "");
    form.append("disclaimer", clinicDet?.disclaimer ?? "");
    form.append("address", entityProfiles?.clinic?.address ?? "");
    form.append("pharmacyName", entityProfiles?.pharmacy?.name ?? "");
    form.append("pharmacyAddress", entityProfiles?.pharmacy?.address ?? "");
    form.append("dlNo", entityProfiles?.pharmacy?.dlNo ?? "");
    form.append("petShopName", entityProfiles?.petshop?.name ?? "");
    form.append("petShopAddress", entityProfiles?.petshop?.address ?? "");
    form.append("patientIdSeries", patientIdSeries ?? "");
    form.append("termsRequest", JSON.stringify(reqList));
    const keepCombined = true;
    // (clinicDet?.billingMode ?? "")?.toLowerCase() === "combined" ||
    // clinicDet?.billingMode === null;
    form.append("billingMode", keepCombined ? "Combined" : "Separate");
    form.append("temperature", temperatureUnit ?? "C");
    form.append("weight", weightUnit ?? "kg");
    const autoApproveOnline = !!autoApproveOnlineBook;
    const onlineAccess = normalizeEditConsultAccess(onlineBookAccess);
    const onlineRoles = autoApproveOnline ? [] : onlineAccess.roles;
    const onlineUserIds = autoApproveOnline
      ? []
      : toUserIds(onlineAccess.users);
    form.append("autoApproveOnlineBook", String(autoApproveOnline));
    form.append("onlineBookAllowedRoles", safeJsonStringify(onlineRoles, "[]"));
    form.append(
      "onlineBookAllowedUsers",
      safeJsonStringify(onlineUserIds, "[]"),
    );
    const closeDaysToSave = Number(autoCloseDays);
    form.append(
      "autoCloseAfterDays",
      String(
        Math.max(
          1,
          Math.min(5, Number.isFinite(closeDaysToSave) ? closeDaysToSave : 3),
        ),
      ),
    );

    const allowEdit = !!allowEditConsultations;
    const access = normalizeEditConsultAccess(editConsultAccess);
    const editRoles = allowEdit ? access.roles : [];
    const editUserIds = allowEdit ? toUserIds(access.users) : [];
    form.append("allowEditConsultations", String(allowEdit));
    form.append("editConsultationRoles", safeJsonStringify(editRoles, "[]"));
    form.append("editConsultationUsers", safeJsonStringify(editUserIds, "[]"));
    try {
      console.log("editConsultationAccess save", {
        allowEditConsultations: allowEdit,
        roles: editRoles,
        userIds: editUserIds,
      });
    } catch {
      // ignore logging errors
    }

    return dispatch(updateClinic(form)).then((res) => {
      if (res?.payload) {
        try {
          const prevClinic = readMandateSession()?.clinic ?? {};
          patchMandateSession({
            clinic: {
              ...prevClinic,
              ...(clinicDet && typeof clinicDet === "object" ? clinicDet : {}),
              temperature: temperatureUnit ?? prevClinic?.temperature ?? "C",
              weight: weightUnit ?? prevClinic?.weight ?? "kg",
              patientIdSeries: patientIdSeries ?? "",
            },
          });
        } catch {
          // ignore mandate patch errors
        }
        loadClinicProfile();
      }
      return res;
    });
  };

  const handleSave = () => {
    if (allowEditConsultations) {
      const access = normalizeEditConsultAccess(editConsultAccess);
      if (!access.roles.length && !access.users.length) {
        setEditConsultAccessError(true);
        return;
      }
      setEditConsultAccessError(false);
    }

    if (!autoApproveOnlineBook) {
      const access = normalizeEditConsultAccess(onlineBookAccess);
      if (!access?.roles?.length && !access?.users?.length) {
        setOnlineBookAccessError(true);
        return;
      }
      setOnlineBookAccessError(false);
    }

    if (!showSharedTag) {
      const emrPayload = {
        ...emrFields,
        internalNotes: false,
      };
      dispatch(updateEmrSettings(emrPayload));
    }

    handleClinicProfileSave().then(() => {
      handleGlobalTimeSave();
    });
  };

  const currentProfile = entityProfiles?.[activeEntity] ?? {
    name: "",
    taxId: "",
    address: "",
    dlNo: "",
  };
  const currentHolidays = Array.isArray(holidays) ? holidays : [];
  const editConsultRoles = Array.isArray(editConsultAccess?.roles)
    ? editConsultAccess.roles
    : [];
  const editConsultUsers = Array.isArray(editConsultAccess?.users)
    ? editConsultAccess.users
    : [];
  const showEditConsultPersonSelect = !isClinicOwnerOnly(editConsultRoles);
  const editConsultPersonOptions = showEditConsultPersonSelect
    ? getUsersForRoles(editConsultRoles)
    : [];
  const selectedEditConsultUserIds = new Set(
    editConsultUsers.map((user) => getUserOptionId(user)),
  );
  const selectedEditConsultPersons = editConsultPersonOptions.filter((option) =>
    selectedEditConsultUserIds.has(String(option?.value ?? "")),
  );
  const onlineBookRoles = Array.isArray(onlineBookAccess?.roles)
    ? onlineBookAccess.roles
    : [];
  const onlineBookUsers = Array.isArray(onlineBookAccess?.users)
    ? onlineBookAccess.users
    : [];
  const requiresOnlineBookApproval = !autoApproveOnlineBook;
  const showOnlineBookPersonSelect = !isClinicOwnerOnly(onlineBookRoles);
  const onlineBookPersonOptions = showOnlineBookPersonSelect
    ? getUsersForRoles(onlineBookRoles)
    : [];
  const selectedOnlineBookUserIds = new Set(
    onlineBookUsers.map((user) => getUserOptionId(user)),
  );
  const selectedOnlineBookPersons = onlineBookPersonOptions.filter((option) =>
    selectedOnlineBookUserIds.has(String(option?.value ?? "")),
  );

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10">
        <div>
          <Typography className="text-bold fs20 black">
            Consultation Settings
          </Typography>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Booking behaviour, entity operating hours, consent/color mapping,
            and EMR visibility.
          </Typography>
        </div>
        <div>
          <CustomButton text="Save Changes" onClick={handleSave} smallBtn />
        </div>
      </div>
      <Divider />

      {/* Booking behaviour */}
      <div className="back-white br12 border p20 mt20 mb20">
        <div className="py12">
          <div
            className="flex-row-between mb10 gap15"
            style={{ alignItems: "flex-start" }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography className="text-bold fs14 black">
                Allow online booking
              </Typography>
              <Typography className="txt-regular fs12 card-gray-color mt2">
                Customers can book appointments themselves through the patient
                app / booking widget.
              </Typography>
            </div>
            <div
              className="bf-approve-switch flex-shrink-0"
              style={{ marginTop: 2 }}
            >
              <Typography
                className={`txt-regular fs12 ${
                  !requiresOnlineBookApproval ? "blue-color" : "card-gray-color"
                }`}
              >
                Auto approve
              </Typography>
              <CustomSwitch
                value={requiresOnlineBookApproval}
                onChange={(e) => toggleAutoApproveOnlineBook(!e?.target?.value)}
                grayToBlue
              />
              <Typography
                className={`txt-regular fs12 ${
                  requiresOnlineBookApproval ? "blue-color" : "card-gray-color"
                }`}
              >
                Requires approval
              </Typography>
            </div>
          </div>

          {requiresOnlineBookApproval ? (
            <div className="bf-tier-card mt15">
              <div>
                <Typography className="txt-regular fs12 card-gray-color mb8">
                  By role
                </Typography>
                <div className="bf-chip-row">
                  {editConsultRoleOptions.map((role) => {
                    const selected = onlineBookRoles.includes(role.code);
                    return (
                      <button
                        key={role.code}
                        type="button"
                        className={`bf-role-chip${
                          selected ? " bf-role-chip-active" : ""
                        }`}
                        onClick={() => toggleOnlineBookRole(role.code)}
                      >
                        <span className="bf-chip-check">
                          {selected ? "✓" : ""}
                        </span>
                        {role.label}
                      </button>
                    );
                  })}
                </div>
                {onlineBookAccessError ? (
                  <FormHelperText error>
                    Select at least one role or specific person
                  </FormHelperText>
                ) : null}
              </div>

              {showOnlineBookPersonSelect ? (
                <div className="mt15">
                  <Typography className="txt-regular fs12 card-gray-color mb8">
                    By specific person (optional if a role is selected)
                  </Typography>
                  <div className="bf-person-select">
                    <Select
                      multiSelectSearchCheck
                      label=""
                      list={onlineBookPersonOptions}
                      value={selectedOnlineBookPersons}
                      handleChange={(e) =>
                        updateOnlineBookUsers(e?.target?.value || [])
                      }
                      disabled={!onlineBookRoles.length}
                      error={onlineBookAccessError && !onlineBookRoles.length}
                    />
                  </div>
                  {!onlineBookRoles.length ? (
                    <Typography className="txt-regular fs11 card-gray-color mt6">
                      Select at least one role to choose people.
                    </Typography>
                  ) : null}
                  {onlineBookAccessError && !onlineBookRoles.length ? (
                    <FormHelperText error>
                      Select at least one role or specific person
                    </FormHelperText>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="mv5">
          <Divider />
        </div>
        <div className="flex-row-between-align-center py12">
          <div style={{ maxWidth: "70%" }}>
            <Typography className="text-bold fs14 black">
              Auto-close appointments
            </Typography>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Automatically end consult, finalize the bill, and complete the
              appointment after this many days of inactivity.
            </Typography>
          </div>
          <div className="flex-row-ali-cen" style={{ gap: 8 }}>
            <button
              type="button"
              style={stepperBtn}
              onClick={() => setAutoCloseDays((prev) => Math.max(1, prev - 1))}
            >
              −
            </button>
            <div
              style={{
                minWidth: 44,
                height: 32,
                borderRadius: 8,
                border: "1px solid #D1D5DB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {autoCloseDays}d
            </div>
            <button
              type="button"
              style={stepperBtn}
              onClick={() => setAutoCloseDays((prev) => Math.min(5, prev + 1))}
            >
              +
            </button>
          </div>
        </div>
        <div className="mv5">
          <Divider />
        </div>
        <div className="py12">
          <div className="flex-row-between-align-center">
            <div style={{ maxWidth: "75%" }}>
              <Typography className="text-bold fs14 black">
                Allow Edit Consultations
              </Typography>
              <Typography className="txt-regular fs12 card-gray-color mt2">
                Allow vets to edit EMR records and consultation details after a
                consultation has been closed.
              </Typography>
            </div>
            <CustomSwitch
              value={allowEditConsultations}
              onChange={toggleAllowEditConsultations}
              grayToBlue
            />
          </div>

          {allowEditConsultations ? (
            <div className="bf-tier-card mt15">
              <div>
                <Typography className="txt-regular fs12 card-gray-color mb8">
                  By role
                </Typography>
                <div className="bf-chip-row">
                  {editConsultRoleOptions.map((role) => {
                    const selected = editConsultRoles.includes(role.code);
                    return (
                      <button
                        key={role.code}
                        type="button"
                        className={`bf-role-chip${
                          selected ? " bf-role-chip-active" : ""
                        }`}
                        onClick={() => toggleEditConsultRole(role.code)}
                      >
                        <span className="bf-chip-check">
                          {selected ? "✓" : ""}
                        </span>
                        {role.label}
                      </button>
                    );
                  })}
                </div>
                {editConsultAccessError ? (
                  <FormHelperText error>
                    Select at least one role or specific person
                  </FormHelperText>
                ) : null}
              </div>

              {showEditConsultPersonSelect ? (
                <div className="mt15">
                  <Typography className="txt-regular fs12 card-gray-color mb8">
                    By specific person (optional if a role is selected)
                  </Typography>
                  <div className="bf-person-select">
                    <Select
                      multiSelectSearchCheck
                      label=""
                      list={editConsultPersonOptions}
                      value={selectedEditConsultPersons}
                      handleChange={(e) =>
                        updateEditConsultUsers(e?.target?.value || [])
                      }
                      disabled={!editConsultRoles.length}
                      error={editConsultAccessError && !editConsultRoles.length}
                    />
                  </div>
                  {!editConsultRoles.length ? (
                    <Typography className="txt-regular fs11 card-gray-color mt6">
                      Select at least one role to choose people.
                    </Typography>
                  ) : null}
                  {editConsultAccessError && !editConsultRoles.length ? (
                    <FormHelperText error>
                      Select at least one role or specific person
                    </FormHelperText>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Entities & operating hours */}
      <div className="back-white br12 border p20 mb20">
        <Typography className="text-bold fs15 black">
          Entities &amp; operating hours
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb15">
          Each entity under this branch keeps its own profile and schedule — a
          pharmacy&apos;s hours don&apos;t have to match the clinic&apos;s.
        </Typography>

        <div className="flex-row mb15" style={{ gap: 8, flexWrap: "wrap" }}>
          {entitiesList.map((ent) => {
            const selected = activeEntity === ent.id;
            return (
              <button
                key={ent.id}
                type="button"
                onClick={() => setActiveEntity(ent.id)}
                style={{
                  ...pillBase,
                  borderColor: selected ? "#93C5FD" : "#E5E7EB",
                  background: selected ? "#EFF6FF" : "#fff",
                  color: selected ? "#1D4ED8" : "#111827",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {ent.label}
              </button>
            );
          })}
        </div>

        <Grid container spacing={2} className="mb10 mt5">
          <Grid item xs={12} md={activeEntity === "pharmacy" ? 4 : 8}>
            <CustomTextField
              label="Entity name"
              value={currentProfile?.name ?? ""}
              handleChange={(e) => updateEntityProfile("name", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              label="Tax ID"
              value={currentProfile?.taxId ?? ""}
              handleChange={(e) => updateEntityProfile("taxId", e.target.value)}
              placeholder="—"
              fullWidth
            />
          </Grid>
          {activeEntity === "pharmacy" ? (
            <Grid item xs={12} md={4}>
              <CustomTextField
                label="Drug License No"
                value={currentProfile?.dlNo ?? ""}
                handleChange={(e) =>
                  updateEntityProfile("dlNo", e.target.value)
                }
                placeholder=""
                fullWidth
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <CustomTextField
              label="Address"
              value={currentProfile?.address ?? ""}
              handleChange={(e) =>
                updateEntityProfile("address", e.target.value)
              }
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
        </Grid>

        {activeEntity === "clinic" ? (
          <div className="mb20">
            <Typography className="text-bold fs14 black mb10">
              Consultation types offered
            </Typography>
            <div className="flex-row" style={{ gap: 10, flexWrap: "wrap" }}>
              {consultTypeOptions.map(({ key, label, Icon }) => {
                const selected = !!consultTypes?.[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setConsultTypes((prev) => ({
                        ...prev,
                        [key]: !prev?.[key],
                      }))
                    }
                    style={{
                      ...pillBase,
                      borderColor: selected ? "#93C5FD" : "#D1D5DB",
                      background: selected ? "#EFF6FF" : "#fff",
                      color: selected ? "#1D4ED8" : "#374151",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: selected
                          ? "1px solid #2563EB"
                          : "1px solid #D1D5DB",
                        background: selected ? "#2563EB" : "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selected ? (
                        <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
                      ) : null}
                    </span>
                    <Icon sx={{ fontSize: 18 }} />
                    {label}
                  </button>
                );
              })}
            </div>
            <Typography className="text-bold fs14 black mt10">
              Patient ID series
            </Typography>
            <Grid container className="mt5">
              <Grid item xs={12} md={4}>
                <CustomTextField
                  label=""
                  value={patientIdSeries}
                  handleChange={(e) => setPatientIdSeries(e.target.value)}
                  placeholder="Patient ID series"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
        ) : null}

        <Typography className="text-bold fs14 black mb10">
          Operating hours
        </Typography>
        <div className="mb15">
          <div className="flex-row-ali-cen mb8">
            <div className="w5Per" />
            <div className="w15Per">
              <Typography className="font-bold fs14 blue-color">Day</Typography>
            </div>
            <div className="w20Per">
              <Typography className="font-bold fs14 blue-color">
                Start Time
              </Typography>
            </div>
            <div className="w20Per">
              <Typography className="font-bold fs14 blue-color">
                End Time
              </Typography>
            </div>
            <div className="w10Per" />
          </div>
          <div className="box-hor-split mb5" />
          {dayValues?.length > 0 ? (
            dayValues.map((pd, i) => (
              <div key={pd?.day + i}>
                {pd?.slots?.length > 0 &&
                  pd?.slots?.map((slt, ind) => (
                    <div
                      className="flex-row-ali-cen mv3"
                      key={`${pd?.day}-${ind}-${slt?.startTime}`}
                    >
                      <div className="w5Per pl10">
                        {ind === 0 ? (
                          <CustomCheckbox
                            checked={pd?.isSelected}
                            onChange={() => handleDaySelect(i, !pd?.isSelected)}
                          />
                        ) : null}
                      </div>
                      <div className="w15Per">
                        {ind === 0 ? (
                          <Typography className="txt-regular fs14 blue-color">
                            {pd?.day}
                          </Typography>
                        ) : null}
                      </div>
                      <div className="w20Per pr10">
                        <CustomTextField
                          name="startTime"
                          fullWidth
                          mobileTime
                          handleChange={(e) =>
                            handleChangeDateValue(
                              "startTime",
                              e?.target?.value,
                              i,
                              ind,
                            )
                          }
                          value={slt?.startTime}
                          is12HourFomat={false}
                        />
                      </div>
                      <div className="w20Per pr10">
                        <CustomTextField
                          name="endTime"
                          fullWidth
                          mobileTime
                          handleChange={(e) =>
                            handleChangeDateValue(
                              "endTime",
                              e?.target?.value,
                              i,
                              ind,
                            )
                          }
                          value={slt?.endTime}
                          is12HourFomat={false}
                        />
                      </div>
                      <div className="w10Per">
                        <div className="flex-row-ali-cen">
                          {pd?.slots?.length > 1 ? (
                            <img
                              src={require("../../../assets/images/png/minus.png")}
                              alt=""
                              className="h20 cursor"
                              onClick={() => handleDeleteDateRow(i, ind)}
                            />
                          ) : (
                            <div className="h20 w20px" />
                          )}
                          {ind === 0 && (
                            <img
                              src={require("../../../assets/images/png/PlusIcon.png")}
                              alt=""
                              className="h20 cursor ml10"
                              onClick={() => handleAddDateRow(i)}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                {i < dayValues.length - 1 ? (
                  <div className="box-hor-split w50Per mv5" />
                ) : null}
              </div>
            ))
          ) : (
            <div className="no-rec h100">No records available</div>
          )}
          {noSlotErr && (
            <FormHelperText error>Please add atleast 1 record</FormHelperText>
          )}
          {timeErr && (
            <FormHelperText error>
              Please provide valid time. End Time should be greater than the
              Start Time
            </FormHelperText>
          )}
        </div>

        <div className="flex-row-ali-cen mb20">
          <Typography className="txt-semi-bold black fs14">
            Slot Duration:
          </Typography>
          <div className="ml10 w10Per">
            <CustomTextField
              name="duration"
              fullWidth
              value={duration}
              handleChange={(e) => setDuration(e?.target?.value)}
              endIcon
              inputIcon="Mins"
            />
          </div>
        </div>

        <Typography className="text-bold fs14 black mb10">
          Holiday calendar
        </Typography>
        {(currentHolidays ?? []).map((hol, idx) => (
          <div key={`${hol?.holliId ?? hol?.date}-${idx}`}>
            <div className="flex-row-between-align-center py10">
              <Typography className="fs13 black" style={{ minWidth: 110 }}>
                {hol?.date ? moment(new Date(hol.date)).format("MMMM DD") : ""}
              </Typography>
              <Typography className="fs13 black" style={{ flex: 1 }}>
                {hol?.holliday ?? hol?.occasion ?? ""}
              </Typography>
              <button
                type="button"
                onClick={() => handleRemoveHoliday(hol?.holliId)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#9CA3AF",
                  padding: 4,
                }}
                aria-label="Remove holiday"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <Divider />
          </div>
        ))}

        <Grid container spacing={1} className="mt10" alignItems="center">
          <Grid item xs={12} sm={4}>
            <CustomTextField
              name="holidayDate"
              value={newHolidayDate}
              handleChange={(e) => setNewHolidayDate(e?.target?.value ?? null)}
              fullWidth
              mobileDate
              placeholder="Date"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTextField
              placeholder="Occasion"
              value={newHolidayOccasion}
              handleChange={(e) => {
                setNewHolidayOccasion(e.target.value);
                setOccasionError(
                  String(e.target.value ?? "").trim().length === 0,
                );
              }}
              fullWidth
              error={occasionError}
              helperText={occasionError ? "Occasion is required field" : ""}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <button
              type="button"
              onClick={handleAddHoliday}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: "1px solid #D1D5DB",
                background: "#fff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Add holiday"
            >
              <AddIcon sx={{ fontSize: 20, color: "#374151" }} />
            </button>
          </Grid>
        </Grid>
      </div>

      {/* Consent forms & colors */}
      {/* <div className="back-white br12 border p20 mb20">
        <Typography className="text-bold fs15 black">
          Consent forms &amp; color codes by service type
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb15">
          Map required consent forms and a calendar color to each service type.
        </Typography>

        <div
          className="flex-row-ali-cen mb10"
          style={{ gap: 12, padding: "0 4px" }}
        >
          <div style={{ width: "18%", ...sectionHeaderStyle }}>
            Service Type
          </div>
          <div style={{ flex: 1, ...sectionHeaderStyle }}>Consent Forms</div>
          <div
            style={{
              width: 56,
              textAlign: "right",
              ...sectionHeaderStyle,
            }}
          >
            Color
          </div>
        </div>

        {serviceTypes.map((srv, index) => {
          const selectedConsents = Array.isArray(consentMapping?.[srv])
            ? consentMapping[srv]
            : [];
          return (
            <div key={srv}>
              <div
                className="flex-row py12"
                style={{
                  gap: 12,
                  alignItems: "flex-start",
                  padding: "0 4px",
                  marginTop: 5,
                  marginBottom: 5,
                }}
              >
                <div
                  style={{
                    width: "18%",
                    fontWeight: 700,
                    fontSize: 13,
                    paddingTop: 8,
                  }}
                  className="black"
                >
                  {srv}
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  {consentOptions.map((consent) => {
                    const selected = selectedConsents.includes(consent);
                    return (
                      <button
                        key={consent}
                        type="button"
                        onClick={() => toggleConsent(srv, consent)}
                        style={{
                          ...pillBase,
                          borderColor: selected ? "#93C5FD" : "#D1D5DB",
                          background: selected ? "#EFF6FF" : "#fff",
                          color: selected ? "#1D4ED8" : "#4B5563",
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "6px 10px",
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: selected
                              ? "1px solid #2563EB"
                              : "1px solid #D1D5DB",
                            background: selected ? "#2563EB" : "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {selected ? (
                            <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                          ) : null}
                        </span>
                        {consent}
                      </button>
                    );
                  })}
                </div>
                <div
                  style={{
                    width: 56,
                    display: "flex",
                    justifyContent: "flex-end",
                    position: "relative",
                    paddingTop: 6,
                  }}
                  ref={colorPickerFor === srv ? colorPickerRef : null}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setColorPickerFor((prev) => (prev === srv ? null : srv))
                    }
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: "1px solid #E5E7EB",
                      background: serviceColors?.[srv] ?? "#0054A6",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    aria-label={`Pick color for ${srv}`}
                  />
                  {colorPickerFor === srv ? (
                    <div
                      style={{
                        position: "absolute",
                        top: 34,
                        right: 0,
                        zIndex: 5,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 10,
                        padding: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 22px)",
                        gap: 8,
                      }}
                    >
                      {colorPalette.map((clr) => (
                        <button
                          key={clr}
                          type="button"
                          onClick={() => {
                            setServiceColors((prev) => ({
                              ...prev,
                              [srv]: clr,
                            }));
                            setColorPickerFor(null);
                          }}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            border:
                              serviceColors?.[srv] === clr
                                ? "2px solid #111827"
                                : "1px solid #E5E7EB",
                            background: clr,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              {index < serviceTypes.length - 1 ? <Divider /> : null}
            </div>
          );
        })}
      </div> */}

      {/* EMR sharing */}
      <div className="back-white br12 border p20">
        <div className="flex-row-ali-cen">
          <Typography className="text-bold fs15 black">
            EMR/Case Summary sharing with customer
          </Typography>
          {showSharedTag ? (
            <Typography className="white-color fs12 con-fit card-top-orange-color ph10 br10 ml10">
              Organisation default
            </Typography>
          ) : null}
        </div>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb10">
          Choose which record fields the customer can see in the portal / shared
          EMR.
        </Typography>

        {emrFieldRows.map((row) => (
          <div key={row.key}>
            <div className="flex-row-between-align-center py12 mv10">
              <Typography className="text-bold fs13 black">
                {row.label}
              </Typography>
              <CustomSwitch
                value={!!emrFields?.[row.key]}
                onChange={() =>
                  setEmrFields((prev) => ({
                    ...prev,
                    [row.key]: !prev?.[row.key],
                  }))
                }
                grayToBlue
                disabled={showSharedTag}
              />
            </div>
            <Divider />
          </div>
        ))}

        {/* <div className="flex-row-between-align-center py12 mv10">
          <div>
            <div className="flex-row-ali-cen" style={{ gap: 8 }}>
              <Typography className="text-bold fs13 black">
                Vet&apos;s internal clinical notes
              </Typography>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#FFEDD5",
                  color: "#C2410C",
                  borderRadius: 999,
                  padding: "2px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 12 }} />
                Locked
              </span>
            </div>
            <Typography className="txt-regular fs12 card-gray-color mt2">
              Locked off organization-wide — internal reasoning never appears in
              the customer-facing record.
            </Typography>
          </div>
          <CustomSwitch value={false} disabled grayToBlue />
        </div> */}
      </div>

      {/* Measurement Units */}
      <div className="back-white br12 border p20 mt20">
        <Typography className="text-bold fs15 black">
          Unit Measurement Settings
        </Typography>
        <Typography className="txt-regular fs12 card-gray-color mt2 mb10">
          These units will be used consistently across the profile, vitals,
          reports, and other relevant areas.
        </Typography>

        <div className="flex-row-between-align-center mb10">
          <Typography className="text-bold fs13 black">Temperature</Typography>
          <div className="flex-row-ali-cen">
            <Typography className="font-medium fs14">°C</Typography>
            <div className="ph10 align-center-wh">
              <CustomSwitch
                value={temperatureUnit === "F"}
                onChange={() =>
                  setTemperatureUnit((prev) => (prev === "F" ? "C" : "F"))
                }
                grayToBlue
              />
            </div>
            <Typography className="font-medium fs14">°F</Typography>
          </div>
        </div>
        <Divider />
        <div className="flex-row-between-align-center mt10">
          <Typography className="text-bold fs13 black">Weight</Typography>
          <div className="flex-row-ali-cen">
            <Typography className="font-medium fs14">kg</Typography>
            <div className="ph10 align-center-wh">
              <CustomSwitch
                value={weightUnit === "lb"}
                onChange={() =>
                  setWeightUnit((prev) => (prev === "lb" ? "kg" : "lb"))
                }
                grayToBlue
              />
            </div>
            <Typography className="font-medium fs14">lb</Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationSettingsScreen;
